import {PolIcon} from "components/PolIcon/policon";
import React, {useEffect} from "react";
import ReactTooltip from "react-tooltip";
import DownloadLink from "v4/components/utils/DownloadLink/DownloadLink";
import PolDropdown from "v4/components/ui/PolDropdown/PolDropdown";
import PolPopup from "v4/components/ui/PolPopup/PolPopup";
import GeneratePdf from "v4/features/front/generatePdf/components/GeneratePdf/GeneratePdf";
import PdfHistory from "v4/features/front/generatePdf/components/PdfHistory/PdfHistory";
import usePdfButton from "v4/features/front/generatePdf/components/PdfButton/hooks/usePdfButton";
import {usePolTranslation} from "v4/hooks/usePolTranslation";

import './PdfButton.scss';

export default function PdfButton({files, quoteId, quoteName, updateHandler, variant = 'header', registerOpenModal}) {
    const {t} = usePolTranslation();
    const isListing = variant === 'listing';

    const {
        sortedPdfFiles,
        currentPdf,
        hasPdf,
        isGeneratePopupOpen,
        preselectTemplateId,
        openGeneratePopup,
        openRegeneratePopup,
        closeGeneratePopup,
        isHistoryPopupOpen,
        toggleHistoryPopup,
    } = usePdfButton(files);

    useEffect(() => {
        if (!registerOpenModal) return;
        registerOpenModal(openRegeneratePopup);

        return () => registerOpenModal(null);
    }, [registerOpenModal, openRegeneratePopup]);

    return (
        <>
            {hasPdf
                ? (
                    <PolDropdown
                        anchorInline="right"
                        withPortal={isListing}
                        className={isListing ? 'pdf-button--listing' : ''}
                        summaryRender={({isOpen}) => (
                            <button className={`${isListing ? 'pdf-button__trigger--listing' : 'prospect-tab__prospect__header__action'} pdf-button__trigger ${isOpen ? 'is-active' : ''}`}
                                    disabled={!quoteName}
                                    {...(isListing && {'data-for': 'row-action', 'data-tip': t('pdf_actions')})}>
                                <PolIcon icon="pdf"/>
                                {isListing
                                    ? (
                                        <span className="pdf-button__tab">
                                            <PolIcon icon="chevron-down" className="pdf-button__chevron"/>
                                        </span>
                                    )
                                    : (
                                        <>
                                            {' PDF'}
                                            <PolIcon icon="chevron-down" className="pdf-button__chevron"/>
                                        </>
                                    )
                                }
                            </button>
                        )}
                        detailsRender={({toggleIsOpen}) => (
                            <div className={`pol-dropdown__details ${isListing ? 'pdf-button__details--listing' : ''}`}>
                                {currentPdf?.downloadLink
                                    ? (
                                        <DownloadLink className="pol-dropdown__details-item"
                                                      fileName={currentPdf?.fileLabel}
                                                      href={currentPdf?.downloadLink}
                                                      title="">
                                            <PolIcon icon="download" className="pol-dropdown__details-item__icon"/>
                                            {t('download')}
                                        </DownloadLink>
                                    )
                                    : (
                                        <span className="pol-dropdown__details-item pol-dropdown__details-item--disabled"
                                              title={t('link_unavailable')}>
                                            <PolIcon icon="download" className="pol-dropdown__details-item__icon"/>
                                            {t('download')}
                                        </span>
                                    )
                                }
                                <div className="pol-dropdown__details-item"
                                     onClick={() => {
                                         toggleIsOpen();
                                         openRegeneratePopup();
                                     }}>
                                    <PolIcon icon="refresh" className="pol-dropdown__details-item__icon"/>
                                    {t('regenerate')}
                                </div>
                                <div className="pol-dropdown__details-item"
                                     onClick={() => {
                                         toggleIsOpen();
                                         toggleHistoryPopup(true);
                                     }}>
                                    <PolIcon icon="clock" className="pol-dropdown__details-item__icon"/>
                                    {t('history')}
                                </div>
                            </div>
                        )}
                    />
                )
                : (
                    <>
                        <button className={isListing ? 'pdf-button__trigger--listing' : 'prospect-tab__prospect__header__action'}
                                disabled={!quoteName}
                                onClick={openGeneratePopup}
                                data-for={isListing ? 'row-action' : 'generate-pdf'}
                                data-tip={t('generate_pdf')}>
                            <PolIcon icon="pdf"/>
                        </button>
                        {!isListing && <ReactTooltip id="generate-pdf" effect="solid"/>}
                    </>
                )
            }

            <PolPopup title={t('generate_pdf')}
                      classNames={{content: 'quotes-generate-pdf'}}
                      isOpen={isGeneratePopupOpen}
                      closePopup={closeGeneratePopup}>
                <GeneratePdf toggleGeneratePopup={closeGeneratePopup}
                             preselectTemplateId={preselectTemplateId}
                             quoteId={quoteId}
                             updateHandler={updateHandler}/>
            </PolPopup>

            <PolPopup title={t('history')}
                      classNames={{popup: 'pdf-history-popup', content: 'pdf-history-popup'}}
                      sticked={{header: true}}
                      isOpen={isHistoryPopupOpen}
                      closePopup={() => toggleHistoryPopup(false)}>
                <PdfHistory pdfFiles={sortedPdfFiles}
                            currentPdf={currentPdf}
                            updateHandler={updateHandler}/>
            </PolPopup>
        </>
    );
}
